import React, { useState, useMemo } from 'react';
import { MemberAvatar } from 'app/src/components/MemberAvatar';
import styles from './ChooseMember.less';
import Icon, { IconName } from 'app/common/components/controls/icon';
import { forTemplate } from 'app/common/lib/util/i18n';
import { buildFuzzyMatcher } from 'app/gamma/src/selectors/boards';
import { ComponentWrapper } from 'app/src/components/ComponentWrapper';
import { Button } from 'app/common/components/controls/button/button';
const format = forTemplate('choose_member');

interface Member {
  id: string;
  fullName: string;
}

type MemberGetter<M extends Member> = () => M[];

interface ChooseMemberProps<M extends Member> {
  idMemberInitial: string | undefined;
  getCardMembers: MemberGetter<M>;
  getBoardMembers: MemberGetter<M>;
  onSelect: (member: M | null) => void;
}

interface MemberRowProps {
  member: Member;
  selected: boolean;
  onClick: () => void;
}

const MAX_MEMBERS_IN_SECTION = 12;

const MemberRow: React.FunctionComponent<MemberRowProps> = ({
  member,
  selected,
  onClick,
}) => {
  return (
    <li>
      <Button className={styles.memberButton} onClick={onClick}>
        <MemberAvatar className={styles.memberAvatar} idMember={member.id} />
        <div className={styles.memberName}>{member.fullName}</div>
        {selected && <Icon name={IconName.Check} />}
      </Button>
    </li>
  );
};

function useFilterMembers<M extends Member>(
  search: string,
  getCardMembers: MemberGetter<M>,
  getBoardMembers: MemberGetter<M>,
) {
  return useMemo(() => {
    const matcher = buildFuzzyMatcher(search);
    const isMatch = (member: Member) => matcher(member.fullName);

    const cardMembers = getCardMembers()
      .filter(isMatch)
      .slice(0, MAX_MEMBERS_IN_SECTION);

    const cardMemberIdSet = new Set(cardMembers.map((member) => member.id));

    const boardMembers = getBoardMembers()
      .filter((member) => isMatch(member) && !cardMemberIdSet.has(member.id))
      .slice(0, MAX_MEMBERS_IN_SECTION);

    return { cardMembers, boardMembers };
  }, [search, getCardMembers, getBoardMembers]);
}

export function ChooseMember<M extends Member>(props: ChooseMemberProps<M>) {
  const { idMemberInitial, onSelect } = props;

  const [search, setSearch] = useState('');

  const { cardMembers, boardMembers } = useFilterMembers(
    search,
    props.getCardMembers,
    props.getBoardMembers,
  );

  const renderMembers = (members: M[]) => (
    <ul className={styles.memberList}>
      {members.map((member) => {
        const selected = member.id === idMemberInitial;

        return (
          <MemberRow
            key={member.id}
            member={member}
            selected={selected}
            onClick={() =>
              onSelect(member.id === idMemberInitial ? null : member)
            }
          />
        );
      })}
    </ul>
  );

  return (
    <ComponentWrapper>
      <input
        autoFocus
        className="js-autofocus"
        type="text"
        placeholder={format('search-members')}
        onChange={(e) => setSearch(e.target.value)}
      />
      {cardMembers.length > 0 && (
        <>
          <h4 className={styles.heading}>{format('card-members')}</h4>
          {renderMembers(cardMembers)}
        </>
      )}

      {boardMembers.length > 0 && (
        <>
          <h4 className={styles.heading}>{format('other-board-members')}</h4>
          {renderMembers(boardMembers)}
        </>
      )}
    </ComponentWrapper>
  );
}
